<script async setup>
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useAdminRoles } from '~/composables/permissions/adminRoles'
import { useAlertPermissions } from '~/composables/permissions/alertPermissions'
import { useCasePermissions } from '~/composables/permissions/casePermissions'
import { useFormPermissions } from '~/composables/permissions/formPermissions'
import { usePlacePermissions } from '~/composables/permissions/placePermissions'
import { useUserPermissions } from '~/composables/permissions/userPermissions'

// i18n helpers
const { locales } = useI18n()
let localePath = useLocalePath()
const { locale, changeLocale, applyLocale } = useLocaleManager()

const safeLocalePath = (path) => {
	if (typeof localePath === 'function') {
		return localePath(path)
	}
	// fallback to French, your default
	return `/fr${path.startsWith('/') ? path : '/' + path}`
}

const { loggedIn, user } = useUserSession()
const route = useRoute()

const { isAdminRole, isOfficerRole, isCoordinatorRole, isObserverRole } = await useAdminRoles()
const { canReadAlert } = await useAlertPermissions()
const { canReadCase } = await useCasePermissions()
const { canReadForm } = await useFormPermissions()
const { canCreatePlace } = await usePlacePermissions()
const { canReadUser } = await useUserPermissions()

const projectStore = useProjectStore()
const projectId = ref(null)

// get a reactive ref for selectedProject
// this is stored in local storage to persist across page reloads
let { selectedProject } = storeToRefs(projectStore)

projectId.value = selectedProject.value?.id ?? null


// keep projectId in sync when selectedProject changes
watch(
	selectedProject,
	(newProject) => {
		projectId.value = newProject?.id ?? null
	},
	{ immediate: false }
)

const drawer = ref(false)

const handleDrawerClick = () => {
	if (window.innerWidth < 960) {
		drawer.value = false
	}
}

const publicStore = usePublicStore()
let projects = ref([])

const fetchInstances = async () => {
	//for logged-in users, we must get the list from the projectStore to prime the navigation
	if (route.path.includes('/projects')) {
		const response = await projectStore.getUserProjects()
		if (response.statusCode === 200) {
			projects.value = response.data
			console.log('projectslist', projects.value)
		} else {
			console.error('Error fetching project lists')
		}
	} else {
		//but we get the public list for non-loggedin users, who can define their own language
		const response = await publicStore.fetchProjectList()
		if (response.statusCode === 200) {
			projects.value = response.data
			console.log('projectslist', projects.value)
		} else {
			console.error('Error fetching project lists')
		}
	}


	const projectIdFromRoute = parseInt(route.params.id)
	projectId.value = projectIdFromRoute
	const proj = projects.value.find((p) => p.id === projectIdFromRoute)

	if (proj) {
		projectStore.setSelectedProject({
			id: proj.id,
			name: proj.name,
			defaultLanguage: proj.default_language,
		})

		await applyLocale()
	}
}

const { clear } = useUserSession()

const onLogout = async () => {
	await clear()
	projectStore.clearSelectedProject()
	await navigateTo('/')
}

onBeforeMount(async () => {
	await fetchInstances()
})

// Build localized paths reactively (track locale.value so they recompute on language change)
const workflowPath = computed(() =>
	projectId.value
		? localePath(`/projects/${projectId.value}/workflow`)
		: localePath('/projects')
)
const reportsPath = computed(() =>
	projectId.value
		? localePath(`/projects/${projectId.value}/reports`)
		: localePath('/projects')
)
const casesPath = computed(() =>
	projectId.value
		? localePath(`/projects/${projectId.value}/cases`)
		: localePath('/projects')
)
const dashboardsPath = computed(() =>
	projectId.value
		? localePath(`/projects/${projectId.value}/dashboards`)
		: localePath('/projects')
)
const formsPath = computed(() =>
	projectId.value
		? localePath(`/projects/${projectId.value}/forms`)
		: localePath('/projects')
)
const placesPath = computed(() =>
	projectId.value
		? localePath(`/projects/${projectId.value}/places`)
		: localePath('/projects')
)
const organisationsPath = computed(() =>
	projectId.value
		? localePath(`/projects/${projectId.value}/organisations`)
		: localePath('/projects')
)
const usersPath = computed(() =>
	projectId.value
		? localePath(`/projects/${projectId.value}/users`)
		: localePath('/projects')
)
//alert(reportsPath.value)
</script>

<template>

	<v-app-bar class="pa-1" :elevation="2" style="background-color: #000">
		<v-app-bar-nav-icon
			v-if="loggedIn && !isAdminRole && !$route.path.endsWith('/projects')"
			color="white"
			size="x-large"
			@click="drawer = !drawer"
			class="ma-0 pa-0 custom_navicon"
		></v-app-bar-nav-icon
		><br />

		<ClientOnly>
		<v-app-bar-title v-if="!loggedIn && !selectedProject" class="ma-1">
			<a href="https://www.rainforestfoundationuk.org/our-projects/forestlink/" class="logo" target="_blank"
				><img src="../public/forestlink-white.png" width="200" style="padding: 0px"
			/></a>
			<span class="software_name"
				><a
					style="color: white; text-decoration: none"
					href="https://www.rainforestfoundationuk.org/our-projects/forestlink/"
					target="_blank"
					>{{ $t('general.title') }}</a
				></span
			>
			<br />
			<span class="strapline">{{ $t('general.strapline') }}</span>
		</v-app-bar-title>

		<v-app-bar-title v-if="!loggedIn && selectedProject" class="ma-1">
			<a href="https://www.rainforestfoundationuk.org/our-projects/forestlink/" class="logo" target="_blank"
				><img src="../public/forestlink-white.png" width="200" style="padding: 0px"
			/></a>
			<span class="software_name"
				><a
					style="color: white; text-decoration: none"
					href="https://www.rainforestfoundationuk.org/our-projects/forestlink/"
					target="_blank"
					>{{ $t('general.title') }}</a
				></span
			>
			<span class="strapline" style="font-size: 0.7em; margin-left: 0px">{{
				$t('general.strapline')
			}}</span
			><br />

			<span v-if="selectedProject" class="project_name">{{ selectedProject?.name }}</span>
		</v-app-bar-title>

		<v-app-bar-title v-if="loggedIn && !$route.path.includes('/admin') && $route.path.endsWith('/projects')" class="ma-1">
			<a href="https://www.rainforestfoundationuk.org/our-projects/forestlink/" class="logo" target="_blank"
				><img src="../public/forestlink-white.png" width="200" style="padding: 0px"
			/></a>
			<span class="software_name"
				><a
					style="color: white; text-decoration: none"
					href="https://www.rainforestfoundationuk.org/our-projects/forestlink/"
					target="_blank"
					>{{ $t('general.title') }}</a
				></span
			>
			<br />
			<span class="strapline">{{ $t('general.strapline') }}</span>
		</v-app-bar-title>

		<v-app-bar-title v-if="loggedIn && !$route.path.includes('/admin') && !$route.path.endsWith('/projects')" class="ma-1">

			<span class="software_name"
				><a
					style="color: white; text-decoration: none"
					href="https://www.rainforestfoundationuk.org/our-projects/forestlink/"
					target="_blank"
					>{{ $t('general.title') }}</a
				></span
			>
			
			<span class="strapline">{{ $t('general.strapline') }}</span><br />
			<span class="project_name">{{ selectedProject?.name }}</span>
		</v-app-bar-title>

		<v-app-bar-title v-if="loggedIn && $route.path.includes('/admin')" class="ma-1">
			<a href="https://www.rainforestfoundationuk.org/our-projects/forestlink/" class="logo" target="_blank"
				><img src="../public/forestlink-white.png" width="200" style="padding: 0px"
			/></a>
			<span class="software_name"
				><a
					style="color: white; text-decoration: none"
					href="https://www.rainforestfoundationuk.org/our-projects/forestlink/"
					target="_blank"
					>{{ $t('general.title') }}</a
				></span
			>
			
		</v-app-bar-title>
	</ClientOnly>



		<div v-if="!loggedIn || $route.path.includes('/admin')" class="mr-7 languages">
			<v-btn
				v-for="loc in locales"
				:key="loc.code"
				density="compact"
				size="small"
				:variant="locale === loc.code ? 'flat' : 'outlined'"
				:color="locale === loc.code ? 'primary' : 'white'"
				@click="changeLocale(loc.code)"
				class="ml-2 mt-3 pa-0"
				style="float: left"
			>
				{{ loc.code }}
			</v-btn>
		</div>

		<div v-if="loggedIn">
			<v-chip v-if="isAdminRole" class="ma-2" color="blue" label small>
				RFUK Admin
			</v-chip>
			<v-chip class="ma-2" color="white" label small>
				{{ user.email }}
			</v-chip>
			<v-btn id="user-avatar" v-if="loggedIn" icon>
				<v-avatar size="26px">
					<v-img alt="Avatar" src="../public/avatar.png" />
				</v-avatar>
			</v-btn>

			<v-menu activator="#user-avatar">
				<v-list density="compact">

					<v-list-item
						append-icon="mdi-home-switch"
						:to="safeLocalePath(`/projects`)"
						>
						<v-list-item-title class="text-darken--1">
							{{ $t('users.project_index') }}
						</v-list-item-title>
					</v-list-item>

					<v-list-item
						append-icon="mdi-lock-reset"
						:to="safeLocalePath(`/auth/reset?projectId=${projectId ?? ''}`)"
					>
						<v-list-item-title class="text-darken--1">
							{{ $t('users.pw_change') }}
						</v-list-item-title>
					</v-list-item>
	
					<v-list-item append-icon="mdi-logout" @click="onLogout">
						<v-list-item-title class="text-darken--1">
							{{ $t('users.logout') }}
						</v-list-item-title>
					</v-list-item>

					<v-list-item append-icon="mdi-file-document-check-outline"
					link href="https://docs.google.com/document/d/1xzLzqXgsRL-g2mEREBoWiVh9D8nAymsUPyprIDb5D7A/edit?tab=t.0">
						<v-list-item-title class="text-darken--1">
							{{ $t('general.terms') }}
						</v-list-item-title>
					</v-list-item>

				</v-list>
			</v-menu>
		</div>

		<NuxtLink v-if="!loggedIn">
			<v-btn class="mt-3 logintext" color="white" :to="safeLocalePath('/auth/login')">
				{{ $t('users.login') }}
			</v-btn>
			<v-btn
				class="ma-2"
				color="white"
				icon="mdi-login"
				:to="safeLocalePath('/auth/login')"
			/>
		</NuxtLink>
	</v-app-bar>

	<v-navigation-drawer
		v-model="drawer"
		temporary
		@click="handleDrawerClick"
		v-if="loggedIn"
	>

		<v-list>
			<v-list density="compact" nav>
				<v-list-item style="background-color:#000;">
					<img src="../public/forestlink-white.png" width="200" style="padding: 0px" />
				</v-list-item>
				<v-list-item v-if="canReadAlert && isObserverRole"
					prepend-icon="mdi-form-select"
					value="forms"
					:to="formsPath"
					><NuxtLink class="custom_nav" :to="formsPath">
						{{ $t('forms.title') }}
					</NuxtLink>
				</v-list-item>
				<hr v-if="canReadAlert && isObserverRole" />

				<v-list-item
					v-if="canReadAlert"
					prepend-icon="mdi-file-multiple"
					value="reports"
					:to="reportsPath"
				>
					<NuxtLink class="custom_nav" :to="reportsPath">
						{{ $t('reports.title') }}
					</NuxtLink>
				</v-list-item>
				<v-list-item
					v-if="canReadAlert && !isObserverRole"
					prepend-icon="mdi-chart-box-multiple"
					value="dashboards"
					:to="dashboardsPath"
					><NuxtLink class="custom_nav" :to="dashboardsPath">
						{{ $t('dashboards.title') }}
					</NuxtLink>
				</v-list-item>
				<hr />
				<v-list-item
					v-if="canReadCase"
					prepend-icon="mdi-briefcase-edit"
					value="cases"
					:to="casesPath"
				>
					<NuxtLink class="custom_nav" :to="casesPath">
						{{ $t('cases.title') }}
					</NuxtLink>
				</v-list-item>
				<v-list-item
					v-if="canReadCase && !isObserverRole"
					prepend-icon="mdi-monitor-dashboard"
					value="workflow"
					:to="workflowPath"
				>
					<NuxtLink class="custom_nav" :to="workflowPath">
						{{ $t('workflow.title') }}
					</NuxtLink>
				</v-list-item>
				<hr v-if="canReadCase && !isObserverRole" />

				<v-list-item
					v-if="canReadForm && !isObserverRole"
					prepend-icon="mdi-form-select"
					value="forms"
					:to="formsPath"
					><NuxtLink class="custom_nav" :to="formsPath">
						{{ $t('forms.title') }}
					</NuxtLink>
				</v-list-item>
				<v-list-item
					v-if="canCreatePlace"
					prepend-icon="mdi-map-marker-multiple"
					value="Places"
					:to="placesPath"
				>
					<NuxtLink class="custom_nav" :to="placesPath">
						{{ $t('places.title') }}
					</NuxtLink>
				</v-list-item>
				<hr v-if="canReadForm && !isObserverRole" />

				<v-list-item
					v-if="canReadUser"
					prepend-icon="mdi-account-multiple"
					value="users"
					:to="usersPath"
				>
					<NuxtLink class="custom_nav" :to="usersPath">
						{{ $t('users.title') }}
					</NuxtLink>
				</v-list-item>
				<v-list-item
					v-if="canReadUser"
					prepend-icon="mdi-leaf-circle"
					value="organisations"
					:to="organisationsPath"
				>
					<NuxtLink class="custom_nav" :to="organisationsPath">
						{{ $t('organisations.title') }}
					</NuxtLink>
				</v-list-item>

			</v-list>
		</v-list>
	</v-navigation-drawer>
</template>

<style scoped>
.v-list-item--density-compact.v-list-item--one-line {
	min-height: 40px;
	font-size: 1.1em;
}

.custom_nav {
	font-size: 0.9em;
	text-decoration: none;
	color: black;
}

.software_name {
	text-decoration: none;
	color: white;
	line-height: 0.5em;
	font-size: 0.9em;
	margin-left: 2px;
	margin-right: 0px;
	font-weight: bold;
	filter: drop-shadow(2px 2px #526522);
}

.strapline {
	font-size: 0.7em;
	line-height: 0.5em;
	margin-top: -5px;
	font-weight: bold;
	color: white;
}

.project_name {
	font-weight: bold;
	/*font-family: 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif;*/
	font-family: 'Courier New', Courier, monospace;
	text-decoration: none;
	color: white;
	line-height: 0.8em;
	font-size: 1.4em;
}

.custom_navicon {
	font-size: 35px;
}

.logo {
	margin-left: 10px;
	margin-top: 5px;
	float: left;
	margin-right: 20px;
}

@media (max-width: 550px) {
	.software_name {
		display: none;
	}
	.strapline {
		padding-left: 0px;
		display: none;
	}
	.v-chip {
		display: none;
	}
}

@media (max-width: 900px) {
	.project_name {
		font-size: 1.1em;
	}
	.languages {
		display: none;
	}

	.logintext {
		display: none;
	}
}
</style>
